import type { Space } from '@cocoonspace/sdk-js/types/space'
import { useTranslation } from 'next-i18next'
import { parseAsBoolean, useQueryState } from 'nuqs'
import { useEffect } from 'react'
import { ResizablePanel } from '~/components/molecules/resizable-panel'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '~/components/ui/dialog'
import { StripeProvider } from '~/providers/stripe-provider'
import { useBookingVisitStore } from './space-visit.store'
import { StepperIndicator } from './stepper-indicator'
import { DayStep } from './steps/day-step'
import { HourStep } from './steps/hour-step'
import { IntroStep } from './steps/intro-step'
import { PaymentStep } from './steps/payment-step'

export const useVisitDialog = () => {
	return useQueryState('visitDialog', parseAsBoolean.withDefault(false))
}

export const SpaceVisitStepper = ({
	space,
}: {
	space: Space
}) => {
	const { t } = useTranslation()

	const init = useBookingVisitStore.use.init()
	const step = useBookingVisitStore.use.step()

	useEffect(() => init(), [])

	return (
		<div className='flex flex-col gap-4'>
			<DialogHeader>
				<DialogTitle>
					{step === 'intro' && t('space:visit.dialog.intro.title')}
					{step === 'day' && t('space:visit.dialog.day.title')}
					{step === 'startTime' && t('space:visit.dialog.startTime.title')}
					{step === 'payment' && t('space:visit.dialog.payment.title')}
				</DialogTitle>
			</DialogHeader>

			<div className='flex flex-col gap-4'>
				<StepperIndicator />

				{step === 'intro' && <IntroStep />}
				{step === 'day' && <DayStep />}
				{step === 'startTime' && <HourStep spaceId={space.id} />}
				{step === 'payment' && <PaymentStep space={space} />}
			</div>
		</div>
	)
}

export const SpaceVisitDialog = ({
	space,
}: {
	space: Space
}) => {
	const [isDialogOpen, setIsDialogOpen] = useVisitDialog()

	return (
		<Dialog
			open={isDialogOpen}
			onOpenChange={setIsDialogOpen}
		>
			<DialogContent className='!w-auto !max-w-0 flex min-w-[380px] flex-col gap-4 overflow-hidden pb-8'>
				<StripeProvider>
					<ResizablePanel>
						<SpaceVisitStepper space={space} />
					</ResizablePanel>
				</StripeProvider>
			</DialogContent>
		</Dialog>
	)
}
